import { createFileRoute, Link, redirect, useBlocker, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { userErrorMessage } from "@/lib/user-error";
import { CheckCircle2 } from "lucide-react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ComboboxSearch } from "@/components/ui/combobox-search";
import { COUNTRIES, findCountryByName } from "@/data/geo/countries.ar";
import { CITIES_BY_COUNTRY, getCitiesFor } from "@/data/geo/cities.ar";
import { buildIntakePersonalSchema, type IntakePersonal } from "@/lib/bousala/intake-schema";

import {
  SECTIONS,
  BOUSALA_V1_CONFIG,
  DURATION,
  type Item,
} from "@/data/bousala-v1.config";
import {
  asLang,
  dirFor,
  isRtl,
  tItem,
  tOption,
  tSection,
  tStatic,
  tPreProfile,
  type Lang,
} from "@/lib/bousala/i18n";
import {
  getOrCreateBousalaAttempt,
  savePreProfile,
  saveAnswer,
  setProgress,
  submitAttempt,
  getAttemptAnsweredIds,
} from "@/lib/bousala.functions";
import { SCORING_TOLERANCE } from "@/lib/bousala/scoring";
import { getNonValidityItems } from "@/lib/bousala/items";
import { WelcomeIntro } from "@/components/bousala/welcome-intro";
import { firstNameOf } from "@/lib/bousala/zodiac";
import { generateBousalaReport, getBousalaReport } from "@/lib/bousala/report.functions";
import { getBousalaAccess, getBousalaMode } from "@/lib/bousala-purchase.functions";
import { supabase } from "@/integrations/supabase/client";
import { getSmartPortalState } from "@/lib/bousala/smart-portal.functions";
import { smartFinishDestination } from "@/lib/bousala/smart-entry-step";
import { ensureAnonSession } from "@/lib/auth/anon-session";
import {
  LikertField,
  ScenarioField,
  Top3Field,
  serializeAnswer,
  deserializeAnswer,
  type AnswerValue,
} from "@/components/bousala/item-widgets";
import { BousalaProgressBar, type SectionStatus } from "@/components/bousala/progress-bar";
import { BousalaStagesMenu } from "@/components/bousala/stages-menu";
import { cn } from "@/lib/utils";
import { useScreenFocus } from "@/lib/use-screen-focus";
import { SectionDebrief } from "@/components/bousala/section-debrief";
import { PLAY_EYEBROW, PLAY_GHOST, PLAY_PRIMARY } from "@/components/bousala/play-ui";
import { previousPosition } from "@/lib/bousala/play-nav";
import { AXIS_LINES_AR } from "@/data/bousala-landing";
import { DS_INPUT } from "@/components/ds/field";
import { X, Brain, Briefcase, Leaf, Gauge, Flame, BookOpen, Compass, Lightbulb } from "lucide-react";
import { CompletionSummary } from "@/components/bousala/completion-summary";
import { ReportPreparing } from "@/components/bousala/report-preparing";

const playSearchSchema = z.object({
  // Optional: keeps the URL clean (no trailing "?lang=") when no language is set.
  lang: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/bousala/play")({
  // Client-only: the runner reads localStorage while rendering and the
  // session check below must run in the browser.
  ssr: false,
  validateSearch: zodValidator(playSearchSchema),
  beforeLoad: async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      // No account yet — allowed in while the test is free. The anonymous
      // session is created later, on the first saved answer.
      const mode = await getBousalaMode();
      if (mode.free) return;
      throw redirect({ to: "/bousala" });
    }
    const access = await getBousalaAccess();
    if (access.status === "granted") return;
    if (access.status === "pending") {
      throw redirect({ to: "/bousala/pending" });
    }
    throw redirect({ to: "/bousala" });
  },
  head: () => ({
    meta: [
      { title: "بوصلة د. سارة — الاختبار" },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  component: BousalaPlayer,
});

type StoredAnswer = { v: AnswerValue; ms: number };

const SESSION_PREFIX = "bousala-v1.attempt.";

/** A queued/failed answer write, tracked until the server confirms it. */
type PendingSave = { value: AnswerValue; ms: number; section: string; index: number };
/** Backoff between retries — instant retries just hit the same congestion. */
const SAVE_RETRY_DELAYS = [500, 1500, 4000];
const INTAKE_SESSION_PREFIX = "bousala-v1.intake.";

type FullPreProfile = IntakePersonal & {
  study_status: string;
  stream: string;
  region_now?: string;
  current_college?: string;
  field_of_study?: string;
  current_role?: string;
  years_experience?: string;
};

type ProfilePrefill = {
  full_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
  country: string | null;
  city: string | null;
};

function totalItems() {
  return SECTIONS.reduce((n, s) => n + s.items.length, 0);
}

function BousalaPlayer() {
  const search = Route.useSearch();
  // Prefer the ?lang query param; fall back to a saved choice on the client.
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("bousala.lang") : null;
  const effectiveLangSource = search.lang || stored || "";
  const searchLang = asLang(effectiveLangSource);
  useEffect(() => {
    if (search.lang) {
      try { window.localStorage.setItem("bousala.lang", search.lang); } catch {}
    }
  }, [search.lang]);
  const fnGetOrCreate = useServerFn(getOrCreateBousalaAttempt);
  const fnSavePre = useServerFn(savePreProfile);
  const fnSaveAnswer = useServerFn(saveAnswer);
  const fnSetProgress = useServerFn(setProgress);
  const fnSubmit = useServerFn(submitAttempt);
  const fnAccess = useServerFn(getBousalaAccess);
  const qc = useQueryClient();

  // Anonymous-first: a visitor can open the intro and fill the intake without
  // any account. The session (and the attempt) are created on their first
  // saved step — never on page load, so bot traffic costs us nothing.
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const [anonError, setAnonError] = useState<string | null>(null);
  const attemptIdRef = useRef<string | null>(null);
  const attemptQueryKey = ["bousala", "attempt", effectiveLangSource || "profile"] as const;
  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data: s }) => {
      if (alive) setHasSession(Boolean(s.session));
    });
    return () => {
      alive = false;
    };
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: attemptQueryKey,
    enabled: hasSession === true,
    queryFn: () => fnGetOrCreate({ data: effectiveLangSource ? { language: searchLang } : {} }),
    staleTime: Infinity,
  });

  // Ensure we have (a) a session and (b) an attempt, creating both on demand.
  // Called from the first write the visitor makes — the intake submit or the
  // first answer — never earlier.
  const ensureAttemptId = async (): Promise<string> => {
    if (attemptIdRef.current) return attemptIdRef.current;
    if (data?.attempt?.id) {
      attemptIdRef.current = data.attempt.id;
      return data.attempt.id;
    }
    try {
      await ensureAnonSession();
    } catch {
      setAnonError(
        searchLang === "en"
          ? "We couldn't start your session."
          : "تعذّر بدء الجلسة.",
      );
      throw new Error("ANON_SESSION_FAILED");
    }
    setAnonError(null);
    // Best-effort: registers the free-mode grant for the new session.
    try {
      await fnAccess();
    } catch {}
    // Attempt creation gets one retry: a transient server-function failure
    // (e.g. mid-deploy) must not strand a visitor who already has a session.
    // The session is deliberately kept — the next try reuses it.
    let created: Awaited<ReturnType<typeof fnGetOrCreate>>;
    try {
      created = await fnGetOrCreate({
        data: effectiveLangSource ? { language: searchLang } : {},
      });
    } catch {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        created = await fnGetOrCreate({
          data: effectiveLangSource ? { language: searchLang } : {},
        });
      } catch {
        setAnonError(
          searchLang === "en"
            ? "We couldn't start your assessment."
            : "تعذّر بدء الاختبار.",
        );
        throw new Error("ATTEMPT_CREATE_FAILED");
      }
    }
    attemptIdRef.current = created.attempt.id;
    // Prime the cache so enabling the query doesn't flash a skeleton.
    qc.setQueryData(attemptQueryKey, created);
    setHasSession(true);
    return created.attempt.id;
  };

  if (anonError) {
    const errDir = isRtl(searchLang) ? "rtl" : "ltr";
    const en = searchLang === "en";
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center" dir={errDir}>
        <p className="font-display text-[21px] font-bold text-text-primary">{anonError}</p>
        <p className="mt-2 text-[17px] text-coral-700">
          {en
            ? "Please try again, or create an account to continue."
            : "منجرّب كمان مرة؟ أو إنشاء حساب للمتابعة."}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setAnonError(null)}
            className={PLAY_PRIMARY}
          >
            {en ? "Try Again" : "إعادة المحاولة"}
          </button>
          <Link
            to="/auth"
            search={{ mode: "sign_up", redirect: "/bousala/play" } as any}
            className={cn(PLAY_GHOST, "border border-hairline-default")}
          >
            {en ? "Create an account" : "إنشاء حساب"}
          </Link>
        </div>
      </div>
    );
  }

  if (hasSession === null || (hasSession === true && isLoading)) {
    const loadingDir = isRtl(searchLang) ? "rtl" : "ltr";
    const srMsg = searchLang === "en" ? "Preparing your compass…" : "جاري تجهيز البوصلة…";
    return (
      <div className="min-h-dvh bg-background px-5 py-10" dir={loadingDir}>
        <span className="sr-only" role="status" aria-live="polite">{srMsg}</span>
        <div className="mx-auto max-w-[44rem] animate-pulse space-y-6">
          <div className="h-2 w-full rounded-full bg-cream-300" />
          <div className="h-4 w-1/3 rounded bg-cream-200" />
          <div className="h-8 w-3/4 rounded bg-cream-200" />
          <div className="space-y-2.5">
            <div className="h-12 rounded-ds-md bg-cream-200" />
            <div className="h-12 rounded-ds-md bg-cream-200" />
            <div className="h-12 rounded-ds-md bg-cream-200" />
          </div>
        </div>
      </div>
    );
  }

  if (hasSession === true && (error || !data?.attempt)) {
    const errDir = isRtl(searchLang) ? "rtl" : "ltr";
    const errT = searchLang === "en"
      ? {
          fail: "We couldn't open the test right now.",
          tryLater: "Your account is safe and your access is saved. Try again, and if it still happens we can check it right away.",
          retry: "Try Again",
        }
      : {
          fail: "ما قدرنا نفتح الاختبار هلق.",
          tryLater: "الحساب آمن والوصول محفوظ. منجرّب كمان مرة، وإذا تكرر منراجعه فوراً.",
          retry: "إعادة المحاولة",
        };
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center" dir={errDir}>
        <p className="font-display text-[21px] font-bold text-text-primary">{errT.fail}</p>
        <p className="mt-2 text-[17px] leading-[1.85] text-coral-700">{errT.tryLater}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className={cn(PLAY_PRIMARY, "mt-6")}
        >
          {errT.retry}
        </button>
      </div>
    );
  }

  // Anonymous visitor with no attempt yet: run the intro/intake against a
  // local draft. The real attempt is created on their first save.
  const draftAttempt = {
    id: "draft",
    pre_profile: {},
    answers: {},
    section_progress: { currentSection: "pre", currentIndex: 0 },
    language: searchLang,
  };
  const attempt = (data?.attempt ?? draftAttempt) as any;
  const attemptLang: Lang = asLang(attempt.language ?? "ar");
  return (
    <Runner
      attempt={attempt}
      lang={attemptLang}
      profilePrefill={(data as any)?.profile ?? null}
      onSavePre={async (preProfile) => {
        const attemptId = await ensureAttemptId();
        return fnSavePre({ data: { attemptId, preProfile } });
      }}
      onSaveAnswer={async (itemId, value, ms, currentSection, currentIndex) => {
        const attemptId = await ensureAttemptId();
        return fnSaveAnswer({
          data: { attemptId, itemId, value, ms, currentSection, currentIndex },
        });
      }}
      onSetProgress={async (currentSection, currentIndex) => {
        if (!attemptIdRef.current && !data?.attempt?.id) return;
        const attemptId = await ensureAttemptId();
        return fnSetProgress({ data: { attemptId, currentSection, currentIndex } });
      }}
      onSubmit={async () => {
        const attemptId = await ensureAttemptId();
        return fnSubmit({ data: { attemptId } });
      }}
      onDone={() => qc.invalidateQueries({ queryKey: ["bousala", "attempt"] })}
    />
  );
}

type RunnerProps = {
  attempt: {
    id: string;
    pre_profile: Record<string, string> | any;
    answers: Record<string, StoredAnswer> | any;
    section_progress: { currentSection: string; currentIndex: number } | any;
  };
  lang: Lang;
  profilePrefill: ProfilePrefill | null;
  onSavePre: (preProfile: FullPreProfile) => Promise<unknown>;
  onSaveAnswer: (
    itemId: string,
    value: AnswerValue,
    ms: number,
    currentSection: string,
    currentIndex: number,
  ) => Promise<unknown>;
  onSetProgress: (currentSection: string, currentIndex: number) => Promise<unknown>;
  onSubmit: () => Promise<unknown>;
  onDone: () => void;
};

function Runner({ attempt, lang, profilePrefill, onSavePre, onSaveAnswer, onSetProgress, onSubmit, onDone }: RunnerProps) {
  const dir = dirFor(lang);
  const navigate = useNavigate();
  const initialProgress = (attempt.section_progress ?? { currentSection: "pre", currentIndex: 0 }) as {
    currentSection: string;
    currentIndex: number;
  };
  const initialAnswers = (attempt.answers ?? {}) as Record<string, StoredAnswer>;

  const [phase, setPhase] = useState<string>(initialProgress.currentSection || "pre");
  const [sectionIndex, setSectionIndex] = useState<number>(initialProgress.currentIndex || 0);
  const [answers, setAnswers] = useState<Record<string, StoredAnswer>>(initialAnswers);
  const [saving, setSaving] = useState(false);

  // Background save queue. Still ONE RPC per answer and still non-blocking
  // between questions — but every queued write is TRACKED in `pendingRef`
  // until the server confirms it. A save that never lands stays pending, so
  // `flushSaves()` can honestly report what is still missing instead of
  // resolving cleanly and letting us score against incomplete data.
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingRef = useRef<Map<string, PendingSave>>(new Map());
  const saveWarnedRef = useRef(false);

  // Latest state mirrored for the sessionStorage snapshot (see below).
  const snapshotRef = useRef({ phase, sectionIndex, answers });
  snapshotRef.current = { phase, sectionIndex, answers };
  const writeSnapshot = useCallback(() => {
    try {
      const snap = snapshotRef.current;
      sessionStorage.setItem(
        SESSION_PREFIX + attempt.id,
        JSON.stringify({
          phase: snap.phase,
          sectionIndex: snap.sectionIndex,
          answers: snap.answers,
          pending: Object.fromEntries(pendingRef.current),
        }),
      );
    } catch {}
  }, [attempt.id]);

  const runSave = useCallback(
    async (itemId: string, p: PendingSave) => {
      for (let tryIndex = 0; tryIndex <= SAVE_RETRY_DELAYS.length; tryIndex += 1) {
        try {
          await onSaveAnswer(itemId, p.value, p.ms, p.section, p.index);
          pendingRef.current.delete(itemId);
          writeSnapshot();
          return true;
        } catch {
          const delay = SAVE_RETRY_DELAYS[tryIndex];
          if (delay === undefined) break;
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      // Still pending — deliberately left in the map for the flush/reconcile.
      writeSnapshot();
      if (!saveWarnedRef.current) {
        saveWarnedRef.current = true;
        toast.error("تعذّر حفظ آخر إجابة", {
          description: "الاتصال ممكن يكون ضعيف — منعيد المحاولة قبل إنهاء الاختبار.",
        });
      }
      return false;
    },
    [onSaveAnswer, writeSnapshot],
  );

  const enqueueSave = useCallback(
    (itemId: string, p: PendingSave) => {
      pendingRef.current.set(itemId, p);
      writeSnapshot();
      saveQueueRef.current = saveQueueRef.current.then(async () => {
        const still = pendingRef.current.get(itemId);
        if (!still) return; // already saved by a reconcile pass
        await runSave(itemId, still);
      });
      return saveQueueRef.current;
    },
    [runSave, writeSnapshot],
  );

  // A killed webview (Instagram) can leave confirmed-nowhere writes behind.
  // Re-queue anything the last snapshot still had pending.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_PREFIX + attempt.id);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { pending?: Record<string, PendingSave> };
      for (const [itemId, p] of Object.entries(parsed.pending ?? {})) {
        if (!p || p.value === undefined) continue;
        enqueueSave(itemId, p);
      }
    } catch {}
    // Run once per attempt on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.id]);

  /**
   * Drain the queue and retry anything still pending.
   * Resolves with the set of item ids that are STILL unsaved — never lies.
   */
  const flushSaves = useCallback(async (): Promise<string[]> => {
    await saveQueueRef.current;
    const remaining = Array.from(pendingRef.current.entries());
    for (const [itemId, p] of remaining) {
      await runSave(itemId, p);
    }
    return Array.from(pendingRef.current.keys());
  }, [runSave]);

  // Guest = no real account yet (anonymous session or none). Presentation only.
  const [isGuest, setIsGuest] = useState(false);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data: s }) => {
      setIsGuest(!s.session || s.session.user.is_anonymous === true);
    });
  }, []);
  const [exiting, setExiting] = useState(false);
  // "حفظ وخروج": answers already save in the background; this waits for the
  // queue (existing flushSaves) and only leaves once nothing is pending.
  const saveAndExit = async () => {
    setExiting(true);
    try {
      const left = await flushSaves();
      if (left.length > 0) {
        toast.error(lang === "en" ? "Could not save your last answer" : "تعذّر حفظ آخر إجابة");
        return;
      }
      await navigate({ to: "/bousala" });
    } finally {
      setExiting(false);
    }
  };
  const topBar = (sid: string, prev?: { onPrev?: () => void; disabled: boolean }) => (
    <PlayTopBar
      lang={lang}
      axisIndex={SECTIONS.findIndex((x) => x.id === sid) + 1}
      axisName={tSection(sid, lang).title}
      total={SECTIONS.length}
      onSaveExit={saveAndExit}
      onExit={() => void navigate({ to: "/bousala" })}
      busy={exiting}
      prev={prev}
    />
  );

  const fnGenerateReport = useServerFn(generateBousalaReport);
  const fnAnsweredIds = useServerFn(getAttemptAnsweredIds);
  const [reconciling, setReconciling] = useState(false);
  const [missingIds, setMissingIds] = useState<string[] | null>(null);
  // How many times reconciliation has blocked this session — used for the
  // escape hatch so a structural save failure can't trap the user forever.
  const reconcileBlocksRef = useRef(0);
  // True while the user is filling a gap found during finalization; on save
  // we send them straight back to the final debrief instead of walking forward.
  const [repairing, setRepairing] = useState(false);
  const preparingStartedAtRef = useRef<number>(Date.now());
  // Focus the section-intro heading whenever the phase changes so keyboard and
  // screen-reader users land on the new screen instead of a stale button.
  const introHeadingRef = useScreenFocus<HTMLHeadingElement>(phase);
  useEffect(() => {
    if (phase === "preparing") preparingStartedAtRef.current = Date.now();
  }, [phase]);

  const timeSpentMs = useMemo(() => {
    const started = (attempt as any).started_at
      ? new Date((attempt as any).started_at).getTime()
      : Date.now();
    return Math.max(0, Date.now() - started);
  }, [attempt]);

  const totalCount = useMemo(totalItems, []);
  const allItemIds = useMemo(
    () => SECTIONS.flatMap((s) => s.items.map((it) => it.id)),
    [],
  );
  const inTest = phase !== "pre" && phase !== "welcome" && phase !== "complete";
  const isComplete = phase === "complete";

  // Answers are saved server-side after every step, so in-app navigation
  // never needs a blocking prompt. Keep only the native beforeunload
  // safety net for tab close / refresh (see below).
  useBlocker({
    shouldBlockFn: () => false,
    enableBeforeUnload: () => inTest && !isComplete,
  });

  // Belt-and-suspenders: native beforeunload prompt on tab close / refresh mid-test.
  useEffect(() => {
    if (!inTest || isComplete) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      void flushSaves();
      if (pendingRef.current.size === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [inTest, isComplete, flushSaves]);

  // The Instagram in-app browser backgrounds tabs aggressively and does not
  // reliably fire beforeunload — visibilitychange is the one signal we get.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      if (pendingRef.current.size === 0) return;
      void flushSaves();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [flushSaves]);

  const answeredInSections = useMemo(() => {
    const ids = new Set<string>();
    for (const s of SECTIONS) for (const it of s.items) ids.add(it.id);
    return Object.keys(answers).filter((k) => ids.has(k)).length;
  }, [answers]);

  // ---- Cross-section navigation helpers ----
  const currentSectionRoot = phase.split(":")[0];
  const answeredBySection = useCallback(
    (sid: string) => {
      const sec = SECTIONS.find((s) => s.id === sid);
      if (!sec) return 0;
      let n = 0;
      for (const it of sec.items) if (answers[it.id]?.v !== undefined) n += 1;
      return n;
    },
    [answers],
  );
  const sectionDone = useCallback(
    (sid: string) => {
      const sec = SECTIONS.find((s) => s.id === sid);
      if (!sec) return false;
      return sec.items.every((it) => answers[it.id]?.v !== undefined);
    },
    [answers],
  );
  const statusOf = useCallback<(sid: string) => SectionStatus>(
    (sid) => {
      if (isComplete) return sectionDone(sid) ? "done" : "locked";
      if (sid === currentSectionRoot) return "current";
      if (sectionDone(sid)) return "done";
      return "locked";
    },
    [currentSectionRoot, isComplete, sectionDone],
  );
  const jumpToSection = useCallback(
    (sid: string) => {
      if (statusOf(sid) === "locked") return;
      if (sid === currentSectionRoot && !isComplete) return;
      void goTo(sid, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSectionRoot, isComplete, statusOf],
  );

  // Persist a light copy to sessionStorage for offline resilience
  useEffect(() => {
    writeSnapshot();
  }, [writeSnapshot, phase, sectionIndex, answers]);

  async function goTo(nextPhase: string, nextIndex: number) {
    setPhase(nextPhase);
    setSectionIndex(nextIndex);
    try {
      await onSetProgress(nextPhase, nextIndex);
    } catch {
      // silent; sessionStorage still has state
    }
  }

  /**
   * Never submit against data we only *believe* is saved. Flush the queue,
   * ask the server which item ids it actually holds, re-send anything it is
   * missing (the RPC merges, so this is idempotent), verify, and only then
   * submit. Genuine gaps are surfaced instead of silently scored.
   */
  async function finalizeAndSubmit() {
    setReconciling(true);
    setMissingIds(null);
    try {
      await flushSaves();
      const first = await fnAnsweredIds({ data: { attemptId: attempt.id } });
      let serverIds = new Set<string>(first.ids);
      const localAnswered = allItemIds.filter((id) => answers[id]?.v !== undefined);
      const missingOnServer = localAnswered.filter((id) => !serverIds.has(id));
      if (missingOnServer.length > 0) {
        for (const id of missingOnServer) {
          const a = answers[id];
          if (!a) continue;
          enqueueSave(id, { value: a.v, ms: a.ms ?? 0, section: phase, index: sectionIndex });
        }
        await flushSaves();
        const second = await fnAnsweredIds({ data: { attemptId: attempt.id } });
        serverIds = new Set<string>(second.ids);
      }
      const gaps = allItemIds.filter((id) => !serverIds.has(id));
      // Grade the remaining gaps against the SAME tolerance `scoreAttempt`
      // applies, so we never bounce a finished user back into the test over
      // an item scoring would have absorbed silently.
      if (gaps.length > 0 && gapsExceedTolerance(gaps)) {
        reconcileBlocksRef.current += 1;
        setMissingIds(gaps);
        return;
      }
      await onSubmit();
      onDone();
      setPhase("complete");
      setSectionIndex(0);
    } catch (e: any) {
      toast.error(userErrorMessage(e, lang, lang === "en" ? "Couldn't finish the test" : "تعذّر إنهاء الاختبار"));
    } finally {
      setReconciling(false);
    }
  }

  function locateItem(itemId: string): { sectionId: string; index: number } | null {
    for (const s of SECTIONS) {
      const i = s.items.findIndex((it) => it.id === itemId);
      if (i >= 0) return { sectionId: s.id, index: i };
    }
    return null;
  }

  async function submitAnyway() {
    setMissingIds(null);
    setReconciling(true);
    try {
      await onSubmit();
      onDone();
      setPhase("complete");
      setSectionIndex(0);
    } catch (e: any) {
      toast.error(userErrorMessage(e, lang, lang === "en" ? "Couldn't finish the test" : "تعذّر إنهاء الاختبار"));
    } finally {
      setReconciling(false);
    }
  }

  // ---- Phase: pre-profile ----
  if (phase === "pre") {
    return (
      <PreProfilePhase
        attemptId={attempt.id}
        lang={lang}
        initial={(attempt.pre_profile ?? {}) as Record<string, string>}
        profilePrefill={profilePrefill}
        onSubmit={async (pp) => {
          setSaving(true);
          try {
            await onSavePre(pp);
            await goTo("welcome", 0);
          } catch (e: any) {
            toast.error(userErrorMessage(e, lang, lang === "en" ? "Could not save" : "تعذّر الحفظ"));
          } finally {
            setSaving(false);
          }
        }}
        saving={saving}
        guest={isGuest}
      />
    );
  }

  // ---- Phase: welcome ----
  if (phase === "welcome") {
    return (
      <main dir={dir}>
        <WelcomePhase
          preProfile={(attempt.pre_profile ?? {}) as Record<string, any>}
          lang={lang}
          saving={saving}
          onStart={() => goTo(SECTIONS[0].id + ":intro", 0)}
        />
      </main>
    );
  }

  // ---- Phase: break ----
  if (phase === "break") {
    const midbreakAfter = BOUSALA_V1_CONFIG.midbreak_after_section;
    const nextSectionIdx = SECTIONS.findIndex((s) => s.id === midbreakAfter) + 1;
    const next = SECTIONS[nextSectionIdx];
    const cont = lang === "en" ? "Next part →" : "المحور الجاي ←";
    return (
      <CenterCard dir={dir}>
        <p className="text-[17px] leading-[1.85] text-text-primary">{tStatic("midbreak", lang)}</p>
        <PrimaryButton onClick={() => goTo(next.id + ":intro", 0)} disabled={saving}>
          {cont}
        </PrimaryButton>
      </CenterCard>
    );
  }

  // ---- Phase: complete ----
  if (phase === "complete") {
    return (
      <main className="mx-auto max-w-2xl px-5" dir={dir}>
        <div className={cn("mb-4 flex pt-6", isRtl(lang) ? "justify-start" : "justify-end")}>
          <BousalaStagesMenu
            lang={lang}
            statusOf={statusOf}
            answeredBySection={answeredBySection}
            onJump={jumpToSection}
            align={isRtl(lang) ? "start" : "end"}
          />
        </div>
        <CompletionSummary
          lang={lang}
          answeredCount={answeredInSections}
          totalCount={totalCount}
          totalSections={SECTIONS.length}
          timeSpentMs={timeSpentMs}
          sectionTitles={SECTIONS.map((s) => ({ id: s.id, title: tSection(s.id, lang).title }))}
          onPrepare={() => {
            setPhase("preparing");
            // Kick off (or re-kick) report generation; safe if already scored/ready.
            void fnGenerateReport({ data: { attemptId: attempt.id } }).catch(() => {});
          }}
          onBack={() => navigate({ to: "/portal" })}
        />
      </main>
    );
  }

  // ---- Phase: preparing report ----
  if (phase === "preparing") {
    return (
      <main dir={dir}>
        <PreparingPhase
          lang={lang}
          attemptId={attempt.id}
          startedAtMs={preparingStartedAtRef.current}
          onRetryScoring={async () => {
            // Re-runs scoring, then report generation, for this attempt.
            await onSubmit();
            void fnGenerateReport({ data: { attemptId: attempt.id } }).catch(() => {});
          }}
          onOpenReport={(dest) =>
            dest === "smart"
              ? navigate({ to: "/portal/smart" })
              : navigate({ to: "/portal/bousala/$attemptId", params: { attemptId: attempt.id } })
          }
          onContinueAnyway={() => navigate({ to: "/portal" })}
        />
      </main>
    );
  }

  // ---- Phase: section intro (e.g. "A:intro") ----
  if (phase.endsWith(":intro")) {
    const sid = phase.split(":")[0];
    const section = SECTIONS.find((s) => s.id === sid);
    if (!section) return null;
    const s = tSection(section.id, lang);
    const partLabel = lang === "en" ? `Part ${section.id}` : `المحور ${section.id}`;
    const startBtn = lang === "en" ? "Let's go →" : "يلّا ←";
    const idx = SECTIONS.findIndex((x) => x.id === section.id) + 1;
    const total = SECTIONS.length;
    const remaining = Math.max(0, total - idx);
    const perItemSec = section.id === "H" ? DURATION.per_item_seconds_h : DURATION.per_item_seconds;
    const minutes = Math.max(1, Math.round((section.items.length * perItemSec) / 60));
    const meta = lang === "en"
      ? {
          questions: `${section.items.length} questions`,
          time: `~${minutes} min`,
          stage: `Part ${idx} of ${total}`,
          remaining: remaining > 0 ? `${remaining} left after this` : "final part",
        }
      : {
          questions: `${section.items.length} أسئلة`,
          time: `~${minutes} دقيقة`,
          stage: `المحور ${idx} من ${total}`,
          remaining: remaining > 0 ? `تبقّى ${remaining} بعد هذا` : "المحور الأخير",
        };
    const AxisIcon = AXIS_ICONS[(idx - 1) % AXIS_ICONS.length];
    return (
      <main className="pb-12 md:pb-16" dir={dir}>
        {topBar(section.id)}
        <div className="mx-auto max-w-[44rem] px-5">
          <div className={cn("mb-6 flex", isRtl(lang) ? "justify-start" : "justify-end")}>
            <BousalaStagesMenu
              lang={lang}
              statusOf={statusOf}
              answeredBySection={answeredBySection}
              onJump={jumpToSection}
              align={isRtl(lang) ? "start" : "end"}
            />
          </div>
          <div className="ds-rise">
            <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-ds-md bg-green-100 text-green-800">
              <AxisIcon className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <p className={cn(PLAY_EYEBROW, "mt-5")}>
              {lang === "en" ? `Part ${String(idx).padStart(2, "0")}` : `المحور ${String(idx).padStart(2, "0")}`}
            </p>
            <span className="sr-only">{partLabel}</span>
            <h1
              ref={introHeadingRef}
              tabIndex={-1}
              className="mt-2 font-display text-[30px] font-extrabold leading-[1.35] text-text-primary outline-none"
            >
              {s.title}
            </h1>
            <p className="mt-4 text-[17px] leading-[1.85] text-text-primary">{s.intro || (lang === "en" ? "" : AXIS_LINES_AR[idx - 1] ?? "")}</p>
            {s.note ? (
              <p className="mt-4 rounded-ds-md bg-cream-100 p-4 text-[14px] leading-[1.7] text-brown-500">{s.note}</p>
            ) : null}
            <p className="mt-5 text-[14px] text-brown-500">
              {meta.questions} · {meta.time} · {meta.remaining}
            </p>
            <PrimaryButton onClick={() => goTo(section.id, 0)} disabled={saving}>
              {startBtn}
            </PrimaryButton>
          </div>
        </div>
      </main>
    );
  }

  // ---- Phase: section debrief (e.g. "A:debrief") ----
  if (phase.endsWith(":debrief")) {
    const sid = phase.split(":")[0];
    const idx = SECTIONS.findIndex((s) => s.id === sid);
    const midAfter = BOUSALA_V1_CONFIG.midbreak_after_section;
    const isMidBreakSection = sid === midAfter;
    const nextSection = SECTIONS[idx + 1];

    // Blocking: verifying every answer actually reached the server.
    if (reconciling) {
      const vt = lang === "en"
        ? { title: "Making sure your answers are saved…", body: "This takes a few seconds. Please keep this screen open." }
        : { title: "عم نتأكد إن كل الإجابات انحفظت…", body: "بياخد كم ثانية. الأفضل تضل هالشاشة مفتوحة." };
      return (
        <CenterCard dir={dir}>
          <h1 className="font-display text-[26px] font-extrabold text-text-primary" role="status" aria-live="polite">
            {vt.title}
          </h1>
          <div className="mt-6 h-2 w-full overflow-hidden rounded-ds-pill bg-cream-300" aria-hidden="true">
            <div className="ds-fill-pulse h-full w-2/3 rounded-ds-pill bg-amber-500" />
          </div>
          <p className="mt-4 text-[17px] leading-[1.85] text-text-primary">{vt.body}</p>
        </CenterCard>
      );
    }

    // Real gaps that local state cannot fill — never submit these silently.
    if (missingIds && missingIds.length > 0) {
      const target = locateItem(missingIds[0]);
      const gt = lang === "en"
        ? {
            title: "A few answers are still missing",
            body: `We couldn't find ${missingIds.length} answers on our side. Let's fill them in so your report is accurate.`,
            cta: "Go to the first missing question",
            anyway: "Submit anyway",
          }
        : {
            title: "في إجابات ما وصلتنا",
            body: `ما لقينا ${missingIds.length} من الإجابات عندنا. منكمّلها مع بعض ليطلع التقرير دقيق.`,
            cta: "الذهاب لأول سؤال ناقص ←",
            anyway: "إرسال النتيجة هيك",
          };
      return (
        <CenterCard dir={dir}>
          <h1 className="font-display text-[26px] font-extrabold text-text-primary">{gt.title}</h1>
          <p className="mt-3 text-[17px] leading-[1.85] text-coral-700">{gt.body}</p>
          <PrimaryButton
            onClick={() => {
              setMissingIds(null);
              setRepairing(true);
              if (target) void goTo(target.sectionId, target.index);
            }}
          >
            {gt.cta}
          </PrimaryButton>
          {reconcileBlocksRef.current >= 2 && (
            <button type="button" className={cn(PLAY_GHOST, "mt-3 w-full sm:w-auto")} onClick={() => void submitAnyway()}>
              {gt.anyway}
            </button>
          )}
        </CenterCard>
      );
    }

    return (
      <main className="mx-auto max-w-2xl px-5 pt-6" dir={dir}>
        <div className={cn("mb-3 flex", isRtl(lang) ? "justify-start" : "justify-end")}>
          <BousalaStagesMenu
            lang={lang}
            statusOf={statusOf}
            answeredBySection={answeredBySection}
            onJump={jumpToSection}
            align={isRtl(lang) ? "start" : "end"}
          />
        </div>
        <SectionDebrief
        attemptId={attempt.id}
        sectionId={sid}
        lang={lang}
        disabled={saving}
        onSaveExit={saveAndExit}
        exiting={exiting}
        onContinue={async () => {
          if (isMidBreakSection) {
            await goTo("break", 0);
            return;
          }
          if (nextSection) {
            await goTo(nextSection.id + ":intro", 0);
            return;
          }
          // Last section — reconcile with the server, then submit.
          await finalizeAndSubmit();
        }}
        />
      </main>
    );
  }

  // ---- Phase: within a section ----
  const section = SECTIONS.find((s) => s.id === phase);
  if (!section) {
    // unknown phase → reset
    void goTo("pre", 0);
    return null;
  }
  const activeSection = section;
  const item = activeSection.items[sectionIndex];
  if (!item) return null;

  const isLastOfSection = sectionIndex >= activeSection.items.length - 1;
  const currentAnswer = answers[item.id]?.v;

  // Answers are saved in the background: the answer and the new progress go
  // out as ONE request, queued in order per attempt so the runner never waits
  // on the network between questions.
  function commitAnswer(
    item: Item,
    draft: unknown,
    ms: number,
    nextSection: string,
    nextIndex: number,
  ) {
    const serialized = serializeAnswer(item, draft);
    if (serialized === null) return false;
    setAnswers((prev) => ({ ...prev, [item.id]: { v: serialized, ms } }));
    enqueueSave(item.id, { value: serialized, ms, section: nextSection, index: nextIndex });
    return true;
  }

  function handleNext(draft: unknown, ms: number) {
    // If this answer is repairing a gap found while finalizing, send the user
    // straight back to the final section's debrief instead of the linear flow.
    const lastSection = SECTIONS[SECTIONS.length - 1];
    const nextSection = repairing
      ? lastSection.id + ":debrief"
      : isLastOfSection
        ? activeSection.id + ":debrief"
        : activeSection.id;
    const nextIndex = repairing ? 0 : isLastOfSection ? 0 : sectionIndex + 1;
    if (!commitAnswer(item, draft, ms, nextSection, nextIndex)) return;
    if (repairing) setRepairing(false);
    // Advance immediately; the save is already queued with this exact position.
    setPhase(nextSection);
    setSectionIndex(nextIndex);
  }

  // «السابق»: client-side step back within this attempt (may cross into the
  // previous axis). A changed answer is re-saved through the normal next path.
  const prevPos = repairing ? null : previousPosition(SECTIONS, activeSection.id, sectionIndex);
  function handleBack() {
    if (!prevPos) return;
    setPhase(prevPos.sectionId);
    setSectionIndex(prevPos.index);
  }

  return (
    <ItemScreen
      key={item.id}
      lang={lang}
      item={item}
      section={activeSection}
      sectionIndex={sectionIndex}
      answeredCount={answeredInSections}
      totalCount={totalCount}
      initialDraft={deserializeAnswer(item, currentAnswer)}
      saving={saving}
      onBack={prevPos ? handleBack : undefined}
      onNext={handleNext}
      statusOf={statusOf}
      answeredBySection={answeredBySection}
      onJumpSection={jumpToSection}
      topBar={topBar(activeSection.id, { onPrev: prevPos ? handleBack : undefined, disabled: !prevPos || saving })}
      guest={isGuest}
    />
  );
}

// ---------------- Sub-components ----------------

/**
 * Classify unsaved item ids exactly like `scoreAttempt` does and compare
 * against `SCORING_TOLERANCE`. Validity items never block a submit.
 */
function gapsExceedTolerance(gaps: string[]): boolean {
  const scored = getNonValidityItems();
  const byId = new Map(scored.map((it) => [it.id, it]));
  let structural = 0;
  let iq = 0;
  let bank = 0;
  let bankTotal = 0;
  for (const it of scored) if (it.type !== "top3" && it.type !== "iq") bankTotal += 1;
  for (const id of gaps) {
    const it = byId.get(id);
    if (!it) continue; // validity or unknown item — ignore
    if (it.type === "top3") structural += 1;
    else if (it.type === "iq") iq += 1;
    else bank += 1;
  }
  return (
    structural > SCORING_TOLERANCE.structuralMaxMissing ||
    iq > SCORING_TOLERANCE.iqMaxMissing ||
    bank > bankTotal * SCORING_TOLERANCE.bankMaxMissingRatio
  );
}

const AXIS_ICONS = [Brain, Briefcase, Leaf, Gauge, Flame, BookOpen, Compass, Lightbulb];

function CenterCard({ children, dir = "rtl" }: { children: React.ReactNode; dir?: "rtl" | "ltr" }) {
  return (
    <main className="mx-auto max-w-[44rem] px-5 py-14 ds-rise md:py-20" dir={dir}>
      {children}
    </main>
  );
}

function PrimaryButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(PLAY_PRIMARY, "mt-8 w-full sm:w-auto")}
    >
      {children}
    </button>
  );
}

function PreparingPhase({
  lang,
  attemptId,
  startedAtMs,
  onRetryScoring,
  onOpenReport,
  onContinueAnyway,
}: {
  lang: Lang;
  attemptId: string;
  startedAtMs: number;
  onRetryScoring: () => Promise<void>;
  onOpenReport: (dest: "smart" | "report") => void;
  onContinueAnyway: () => void;
}) {
  const fnGetReport = useServerFn(getBousalaReport);
  const { data, refetch } = useQuery({
    queryKey: ["bousala", "report", attemptId, "preparing"],
    queryFn: () => fnGetReport({ data: { attemptId } }),
    refetchInterval: (q) => {
      const d = q.state.data as { status?: string; scoringError?: boolean } | undefined;
      if (d?.status === "report_ready") return false;
      if (d?.scoringError) return false; // stop polling a known failure
      return 2000;
    },
    refetchOnWindowFocus: false,
  });
  const status = (data as { status?: string } | undefined)?.status ?? null;
  const scoringError = Boolean((data as { scoringError?: boolean } | undefined)?.scoringError);
  const [retrying, setRetrying] = useState(false);
  const [elapsed, setElapsed] = useState<number>(() => Date.now() - startedAtMs);
  useEffect(() => {
    const id = window.setInterval(() => setElapsed(Date.now() - startedAtMs), 500);
    return () => window.clearInterval(id);
  }, [startedAtMs]);
  // Resolve where "open" leads the moment the report is ready, so the button
  // label (free report vs. continue to Smart) never guesses.
  const [dest, setDest] = useState<"smart" | "report" | null>(null);
  useEffect(() => {
    if (status !== "report_ready" || dest !== null) return;
    let cancelled = false;
    void openReportDestination(attemptId).then((d) => {
      if (!cancelled) setDest(d);
    });
    return () => {
      cancelled = true;
    };
  }, [status, dest, attemptId]);
  return (
    <ReportPreparing
      lang={lang}
      status={status}
      elapsedMs={elapsed}
      onOpenReport={() => onOpenReport(dest ?? "report")}
      smartReady={status === "report_ready" ? (dest === null ? null : dest === "smart") : false}
      attemptId={attemptId}
      onContinueAnyway={onContinueAnyway}
      hasError={scoringError}
      retrying={retrying}
      onRetry={async () => {
        setRetrying(true);
        try {
          await onRetryScoring();
        } catch {
          // surfaced by the refetch below
        } finally {
          setRetrying(false);
          void refetch();
        }
      }}
    />
  );
}

function PreProfilePhase({
  attemptId,
  lang,
  initial,
  profilePrefill,
  onSubmit,
  saving,
  guest = false,
}: {
  attemptId: string;
  lang: Lang;
  initial: Record<string, string>;
  profilePrefill: ProfilePrefill | null;
  onSubmit: (pp: FullPreProfile) => Promise<void>;
  saving: boolean;
  guest?: boolean;
}) {
  // Merge priority: attempts.pre_profile (last save) > profile row > sessionStorage.
  const sessionKey = INTAKE_SESSION_PREFIX + attemptId;
  const dir = dirFor(lang);
  const schema = useMemo(() => buildIntakePersonalSchema(lang), [lang]);
  const preQs = useMemo(() => tPreProfile(lang), [lang]);
  const T = lang === "en"
    ? {
        stepBadge: (s: number) => `Before we start · ${s} / 2`,
        h1s: ["A short intro about you", "A few quick questions to fine-tune your compass"],
        subs: [
          "This information is used to personalize your report and analysis.",
          "A few quick questions to help us understand your context.",
        ],
        f: {
          fullname: "Full name",
          dob: "Date of birth",
          dobPh: "Pick your date of birth",
          gender: "Gender",
          male: "Male",
          female: "Female",
          country: "Country of residence",
          countryPh: "e.g. Syria",
          city: "City",
          cityPh: "e.g. Damascus",
          father: "Father's career",
          fatherPh: "e.g. Engineer",
          mother: "Mother's career",
          motherPh: "e.g. Teacher",
          namePh: "e.g. Sara Ahmad",
          noneLabel: "None",
        },
        dobDay: "Day",
        dobMonth: "Month",
        dobYear: "Year",
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        searchPh: "Search…",
        emptyResults: "No results",
        useTyped: (q: string) => `Use "${q}"`,
        selectCountry: "Select country",
        selectCity: "Select city",
        continue: "Continue →",
        back: "← Edit your info",
        start: "Start the compass →",
        careerHeading: "Your work",
        careerHint: "You picked a post-study option — tell us a bit about your work so the report speaks to you.",
        currentRole: "Current or most recent role",
        currentRolePh: "e.g. Marketing manager",
        yearsExp: "Years of professional experience",
        yearsExpPh: "Select a range",
        currentCollege: "College or university",
        currentCollegePh: "e.g. Faculty of Engineering",
        fieldOfStudy: "Field of study / major",
        fieldOfStudyPh: "e.g. Software Engineering",
        yearsExpOptions: [
          { v: "0-1", l: "Less than 1 year" },
          { v: "1-3", l: "1–3 years" },
          { v: "4-6", l: "4–6 years" },
          { v: "7-10", l: "7–10 years" },
          { v: "10+", l: "More than 10 years" },
        ],
      }
    : {
        stepBadge: (s: number) => `قبل ما نبدأ · الخطوة ${s} من 2`,
        h1s: ["تعريف بسيط عنك", "أسئلة سريعة لنضبط بوصلتك"],
        subs: [
          "هذه المعلومات تُستخدم لتخصيص تقريرك الشخصي وتحليل نتائجك.",
          "أسئلة سريعة تساعدنا نفهم سياقك.",
        ],
        f: {
          fullname: "الاسم الكامل",
          dob: "تاريخ الميلاد",
          dobPh: "تاريخ الميلاد",
          gender: "الجنس",
          male: "ذكر",
          female: "أنثى",
          country: "بلد الإقامة",
          countryPh: "مثال: سوريا",
          city: "المدينة",
          cityPh: "مثال: دمشق",
          father: "مهنة الوالد",
          fatherPh: "مثال: مهندس",
          mother: "مهنة الوالدة",
          motherPh: "مثال: معلمة",
          namePh: "مثال: سارة أحمد",
          noneLabel: "لا يوجد",
        },
        dobDay: "اليوم",
        dobMonth: "الشهر",
        dobYear: "السنة",
        months: [
          "يناير",
          "فبراير",
          "مارس",
          "أبريل",
          "مايو",
          "يونيو",
          "يوليو",
          "أغسطس",
          "سبتمبر",
          "أكتوبر",
          "نوفمبر",
          "ديسمبر",
        ],
        searchPh: "بحث…",
        emptyResults: "لا توجد نتائج",
        useTyped: (q: string) => `استخدام "${q}"`,
        selectCountry: "اختيار البلد",
        selectCity: "اختيار المدينة",
        continue: "متابعة ←",
        back: "→ تعديل المعلومات",
        start: "بدء البوصلة ←",
        careerHeading: "عن عملك",
        careerHint: "الخيار يلي انختار بعد انتهاء الدراسة — شوية تفاصيل عن الشغل بتخلّي التقرير يحكي بشكل مناسب.",
        currentRole: "المسمى الوظيفي الحالي أو الأخير",
        currentRolePh: "مثال: مدير تسويق",
        yearsExp: "سنوات الخبرة المهنية",
        yearsExpPh: "اختيار النطاق",
        currentCollege: "الكلية أو الجامعة",
        currentCollegePh: "مثال: كلية الهندسة",
        fieldOfStudy: "التخصص الدراسي",
        fieldOfStudyPh: "مثال: هندسة برمجيات",
        yearsExpOptions: [
          { v: "0-1", l: "أقل من سنة" },
          { v: "1-3", l: "1–3 سنوات" },
          { v: "4-6", l: "4–6 سنوات" },
          { v: "7-10", l: "7–10 سنوات" },
          { v: "10+", l: "أكثر من 10 سنوات" },
        ],
      };

  const initialPersonal: Partial<IntakePersonal> = useMemo(() => {
    let stored: Partial<IntakePersonal> = {};
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (raw) stored = JSON.parse(raw) as Partial<IntakePersonal>;
    } catch {}
    const fromAttempt = initial as Partial<IntakePersonal>;
    const fromProfile: Partial<IntakePersonal> = {
      full_name: profilePrefill?.full_name ?? undefined,
      date_of_birth: profilePrefill?.date_of_birth ?? undefined,
      gender: (profilePrefill?.gender as "male" | "female" | undefined) ?? undefined,
      country: profilePrefill?.country ?? undefined,
      city: profilePrefill?.city ?? undefined,
    };
    return {
      ...stored,
      ...fromProfile,
      ...fromAttempt,
    };
  }, [sessionKey, initial, profilePrefill]);

  const [step, setStep] = useState<1 | 2>(1);
  const [personal, setPersonal] = useState<Partial<IntakePersonal>>(initialPersonal);
  const [personalErrors, setPersonalErrors] = useState<Partial<Record<keyof IntakePersonal, string>>>({});
  const [study, setStudy] = useState<Record<string, string>>({
    study_status: (initial as any).study_status ?? "",
    stream: (initial as any).stream ?? "",
    current_role: (initial as any).current_role ?? "",
    years_experience: (initial as any).years_experience ?? "",
    current_college: (initial as any).current_college ?? "",
    field_of_study: (initial as any).field_of_study ?? "",
  });

  // Persist step-1 typing to sessionStorage.
  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(personal));
    } catch {}
  }, [sessionKey, personal]);

  const setField = <K extends keyof IntakePersonal>(k: K, v: IntakePersonal[K]) => {
    setPersonal((p) => ({ ...p, [k]: v }));
    setPersonalErrors((e) => ({ ...e, [k]: undefined }));
  };

  function handlePersonalNext() {
    const parsed = schema.safeParse(personal);
    if (!parsed.success) {
      const errs: Partial<Record<keyof IntakePersonal, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof IntakePersonal;
        if (!errs[key]) errs[key] = issue.message;
      }
      setPersonalErrors(errs);
      return;
    }
    setPersonal(parsed.data);
    setStep(2);
  }

  // Branching per study_status:
  // - bac        → stream only (no college yet)
  // - uni        → stream + college + field (both required)
  // - graduated  → stream + college + field (both required)
  // - working    → stream + college + field (optional) + role/years
  // - other      → stream only
  const status = study.study_status;
  const showStream = Boolean(status);
  const showCollege = status === "uni" || status === "graduated" || status === "working";
  const collegeRequired = status === "uni" || status === "graduated";
  const needsCareer = status === "working";
  const studyComplete =
    Boolean(status) &&
    (!showStream || Boolean(study.stream)) &&
    (!collegeRequired || (Boolean(study.current_college?.trim()) && Boolean(study.field_of_study?.trim()))) &&
    (!needsCareer || Boolean(study.years_experience));

  const studyStatusQ = preQs.find((q) => q.id === "study_status");
  const streamQ = preQs.find((q) => q.id === "stream");

  function handleStatusChange(nextId: string) {
    setStudy((prev) => {
      const next: Record<string, string> = { ...prev, study_status: nextId };
      const nextShowCollege =
        nextId === "uni" || nextId === "graduated" || nextId === "working";
      const nextNeedsCareer = nextId === "working";
      if (!nextShowCollege) {
        next.current_college = "";
        next.field_of_study = "";
      }
      if (!nextNeedsCareer) {
        next.current_role = "";
        next.years_experience = "";
      }
      return next;
    });
  }

  async function handleStudySubmit() {
    const parsedPersonal = schema.safeParse(personal);
    if (!parsedPersonal.success) {
      setStep(1);
      return;
    }
    await onSubmit({
      ...parsedPersonal.data,
      study_status: study.study_status,
      stream: showStream ? study.stream : "",
      ...(showCollege
        ? {
            current_college: study.current_college?.trim() || undefined,
            field_of_study: study.field_of_study?.trim() || undefined,
          }
        : {}),
      ...(needsCareer
        ? {
            current_role: study.current_role?.trim() || undefined,
            years_experience: study.years_experience || undefined,
          }
        : {}),
    });
    try { sessionStorage.removeItem(sessionKey); } catch {}
  }

  // DOB is edited as three independent selects; keep the parts as their own
  // state so a partial pick (e.g. only the month) doesn't get wiped when the
  // composed value fails the strict YYYY-MM-DD regex.
  const [dobParts, setDobParts] = useState<{ y: string; m: string; d: string }>(() => {
    const iso = personal.date_of_birth;
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split("-");
      return { y, m, d };
    }
    return { y: "", m: "", d: "" };
  });

  const nowYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const out: string[] = [];
    for (let y = nowYear - 13; y >= nowYear - 90; y--) out.push(String(y));
    return out;
  }, [nowYear]);
  const monthOptions = useMemo(
    () => T.months.map((name, i) => ({ value: String(i + 1).padStart(2, "0"), label: `${name} · ${i + 1}` })),
    [T.months],
  );
  const daysInMonth = useMemo(() => {
    const y = Number(dobParts.y);
    const m = Number(dobParts.m);
    if (!y || !m) return 31;
    return new Date(y, m, 0).getDate();
  }, [dobParts.y, dobParts.m]);
  const dayOptions = useMemo(() => {
    const out: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) out.push(String(d).padStart(2, "0"));
    return out;
  }, [daysInMonth]);

  function setDobPart(next: { y?: string; m?: string; d?: string }) {
    const y = next.y ?? dobParts.y;
    const m = next.m ?? dobParts.m;
    let d = next.d ?? dobParts.d;
    if (y && m && d) {
      const max = new Date(Number(y), Number(m), 0).getDate();
      if (Number(d) > max) d = String(max).padStart(2, "0");
    }
    setDobParts({ y, m, d });
    if (y && m && d) {
      setField("date_of_birth", `${y}-${m}-${d}` as IntakePersonal["date_of_birth"]);
    } else {
      // Not enough parts yet — clear the schema value so validation only
      // fires once the user has picked all three.
      setField("date_of_birth", "" as IntakePersonal["date_of_birth"]);
    }
  }

  const selectedCountryCode = useMemo(
    () => findCountryByName(personal.country ?? "")?.code,
    [personal.country],
  );
  const countryOptions = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.code, label: lang === "en" ? c.name_en : c.name_ar })),
    [lang],
  );
  const cityList = getCitiesFor(selectedCountryCode);
  const cityOptions = useMemo(
    () => cityList.map((c) => ({ value: c.name_ar, label: lang === "en" ? c.name_en : c.name_ar })),
    [cityList, lang],
  );

  const h1 = step === 1 ? T.h1s[0] : T.h1s[1];
  const words = h1.split(" ");
  const markAt = words.length > 1 ? 1 : 0;
  return (
    <main className="mx-auto max-w-[44rem] px-5 py-10 md:py-14" dir={dir}>
      <div>
        <div className="mb-8">
          <p className={PLAY_EYEBROW}>{T.stepBadge(step)}</p>
          <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.3] text-text-primary">
            {words.map((w, i) => (
              <span key={i}>
                {i > 0 ? " " : ""}
                {i === markAt ? <mark className="mark bg-transparent text-inherit">{w}</mark> : w}
              </span>
            ))}
          </h1>
          <p className="mt-3 text-[17px] leading-[1.85] text-text-primary">
            {step === 1 ? T.subs[0] : T.subs[1]}
          </p>
        </div>

        {step === 1 ? (
          <div className="space-y-5">
            <FieldRow label={T.f.fullname} error={personalErrors.full_name} htmlFor="fld-fullname">
              <Input className={DS_INPUT}
                id="fld-fullname"
                value={personal.full_name ?? ""}
                onChange={(e) => setField("full_name", e.target.value)}
                maxLength={120}
                autoComplete="name"
                placeholder={T.f.namePh}
                aria-invalid={!!personalErrors.full_name}
              />
            </FieldRow>

            <FieldRow label={T.f.dob} error={personalErrors.date_of_birth} labelId="fld-dob-label">
              <div className="grid grid-cols-3 gap-2" id="fld-dob" role="group" aria-labelledby="fld-dob-label">
                <Select
                  value={dobParts.d}
                  onValueChange={(v) => setDobPart({ d: v })}
                >
                  <SelectTrigger className="min-h-12 rounded-ds-md border-hairline-default bg-surface text-base text-brown-700" aria-label={`${T.f.dob} — ${T.dobDay}`} aria-invalid={!!personalErrors.date_of_birth}>
                    <SelectValue placeholder={T.dobDay} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {dayOptions.map((d) => (
                      <SelectItem key={d} value={d}>{Number(d)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={dobParts.m}
                  onValueChange={(v) => setDobPart({ m: v })}
                >
                  <SelectTrigger className="min-h-12 rounded-ds-md border-hairline-default bg-surface text-base text-brown-700" aria-label={`${T.f.dob} — ${T.dobMonth}`} aria-invalid={!!personalErrors.date_of_birth}>
                    <SelectValue placeholder={T.dobMonth} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={dobParts.y}
                  onValueChange={(v) => setDobPart({ y: v })}
                >
                  <SelectTrigger className="min-h-12 rounded-ds-md border-hairline-default bg-surface text-base text-brown-700" aria-label={`${T.f.dob} — ${T.dobYear}`} aria-invalid={!!personalErrors.date_of_birth}>
                    <SelectValue placeholder={T.dobYear} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </FieldRow>

            <FieldRow label={T.f.gender} error={personalErrors.gender}>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "male" as const, l: T.f.male },
                  { v: "female" as const, l: T.f.female },
                ].map((g) => {
                  const selected = personal.gender === g.v;
                  return (
                    <button
                      key={g.v}
                      type="button"
                      onClick={() => setField("gender", g.v)}
                      aria-pressed={selected}
                      className={cn(
                        "min-h-12 rounded-ds-md border px-5 py-3 font-display text-[16px] font-bold transition-colors duration-ds-fast",
                        selected
                          ? "border-transparent bg-amber-100 text-text-primary ring-1 ring-inset ring-amber-600"
                          : "border-hairline-default bg-surface text-text-primary hover:bg-cream-100",
                      )}
                    >
                      {g.l}
                    </button>
                  );
                })}
              </div>
            </FieldRow>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FieldRow label={T.f.country} error={personalErrors.country} htmlFor="fld-country">
                <ComboboxSearch
                  id="fld-country"
                  value={personal.country ?? ""}
                  onChange={(v) => {
                    setField("country", v);
                    // City list depends on country; clear a city that no
                    // longer matches the new country's list.
                    const nextCode = findCountryByName(v)?.code;
                    const nextCities = nextCode ? (CITIES_BY_COUNTRY[nextCode] ?? []) : [];
                    const currentCity = personal.city ?? "";
                    if (
                      currentCity &&
                      nextCities.length > 0 &&
                      !nextCities.some(
                        (c) => c.name_ar === currentCity || c.name_en === currentCity,
                      )
                    ) {
                      setField("city", "");
                    }
                  }}
                  options={countryOptions}
                  placeholder={T.selectCountry}
                  searchPlaceholder={T.searchPh}
                  emptyLabel={T.emptyResults}
                  customLabel={T.useTyped}
                  aria-invalid={!!personalErrors.country}
                  dir={isRtl(lang) ? "rtl" : "ltr"}
                />
              </FieldRow>
              <FieldRow label={T.f.city} error={personalErrors.city} htmlFor="fld-city">
                {cityOptions.length > 0 ? (
                  <ComboboxSearch
                    id="fld-city"
                    value={personal.city ?? ""}
                    onChange={(v) => setField("city", v)}
                    options={cityOptions}
                    placeholder={T.selectCity}
                    searchPlaceholder={T.searchPh}
                    emptyLabel={T.emptyResults}
                    customLabel={T.useTyped}
                    aria-invalid={!!personalErrors.city}
                    dir={isRtl(lang) ? "rtl" : "ltr"}
                  />
                ) : (
                  <Input className={DS_INPUT}
                    id="fld-city"
                    value={personal.city ?? ""}
                    onChange={(e) => setField("city", e.target.value)}
                    maxLength={80}
                    autoComplete="address-level2"
                    placeholder={T.f.cityPh}
                    aria-invalid={!!personalErrors.city}
                  />
                )}
              </FieldRow>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FieldRow label={T.f.father} error={personalErrors.father_career} htmlFor="fld-father">
                <ParentCareerInput
                  id="fld-father"
                  value={personal.father_career ?? ""}
                  onChange={(v) => setField("father_career", v)}
                  placeholder={T.f.fatherPh}
                  noneLabel={T.f.noneLabel}
                  invalid={!!personalErrors.father_career}
                />
              </FieldRow>
              <FieldRow label={T.f.mother} error={personalErrors.mother_career} htmlFor="fld-mother">
                <ParentCareerInput
                  id="fld-mother"
                  value={personal.mother_career ?? ""}
                  onChange={(v) => setField("mother_career", v)}
                  placeholder={T.f.motherPh}
                  noneLabel={T.f.noneLabel}
                  invalid={!!personalErrors.mother_career}
                />
              </FieldRow>
            </div>

            <div className="mt-8">
              <PrimaryButton disabled={saving} onClick={handlePersonalNext}>
                {T.continue}
              </PrimaryButton>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {studyStatusQ ? (
              <QuestionBlock
                text={studyStatusQ.text_ar}
                options={studyStatusQ.options}
                value={study.study_status}
                onChange={handleStatusChange}
                rtl={isRtl(lang)}
              />
            ) : null}
            {showStream && streamQ ? (
              <QuestionBlock
                text={streamQ.text_ar}
                options={streamQ.options}
                value={study.stream}
                onChange={(v) => setStudy({ ...study, stream: v })}
                rtl={isRtl(lang)}
              />
            ) : null}
            {showCollege ? (
              <>
                <FieldRow label={T.currentCollege} htmlFor="fld-current-college">
                  <Input className={DS_INPUT}
                    id="fld-current-college"
                    value={study.current_college ?? ""}
                    onChange={(e) =>
                      setStudy({ ...study, current_college: e.target.value })
                    }
                    maxLength={120}
                    placeholder={T.currentCollegePh}
                  />
                </FieldRow>
                <FieldRow label={T.fieldOfStudy} htmlFor="fld-field-of-study">
                  <Input className={DS_INPUT}
                    id="fld-field-of-study"
                    value={study.field_of_study ?? ""}
                    onChange={(e) =>
                      setStudy({ ...study, field_of_study: e.target.value })
                    }
                    maxLength={120}
                    placeholder={T.fieldOfStudyPh}
                  />
                </FieldRow>
              </>
            ) : null}
            {needsCareer ? (
              <>
                <FieldRow label={T.currentRole} htmlFor="fld-current-role">
                  <Input className={DS_INPUT}
                    id="fld-current-role"
                    value={study.current_role ?? ""}
                    onChange={(e) =>
                      setStudy({ ...study, current_role: e.target.value })
                    }
                    maxLength={120}
                    placeholder={T.currentRolePh}
                  />
                </FieldRow>
                <FieldRow label={T.yearsExp} htmlFor="fld-years-exp">
                  <Select
                    value={study.years_experience || undefined}
                    onValueChange={(v) =>
                      setStudy({ ...study, years_experience: v })
                    }
                  >
                    <SelectTrigger className="min-h-12 rounded-ds-md border-hairline-default bg-surface text-base text-brown-700" id="fld-years-exp">
                      <SelectValue placeholder={T.yearsExpPh} />
                    </SelectTrigger>
                    <SelectContent>
                      {T.yearsExpOptions.map((o) => (
                        <SelectItem key={o.v} value={o.v}>
                          {o.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>
              </>
            ) : null}
            <div className="mt-8 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" onClick={() => setStep(1)} disabled={saving} className={PLAY_GHOST}>
                {T.back}
              </button>
              <PrimaryButton
                disabled={!studyComplete || saving}
                onClick={handleStudySubmit}
              >
                {T.start}
              </PrimaryButton>
            </div>
          </div>
        )}
        {guest && lang !== "en" ? (
          <p className="mt-6 text-[14px] leading-[1.7] text-brown-500">
            البداية بدون حساب. التقدّم بيتحفظ، والمتابعة بعدين ممكنة بأي وقت.
          </p>
        ) : null}
      </div>
    </main>
  );
}

function FieldRow({
  label,
  error,
  htmlFor,
  labelId,
  children,
}: {
  label: string;
  error?: string;
  htmlFor?: string;
  labelId?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label id={labelId} htmlFor={htmlFor} className="font-display text-[15px] font-bold text-brown-700">
        {label}
      </Label>
      {children}
      {error ? (
        <p role="alert" className="text-[13px] font-bold text-coral-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function QuestionBlock({
  text,
  options,
  value,
  onChange,
  rtl,
}: {
  text: string;
  options: { id: string; label_ar: string }[];
  value: string;
  onChange: (v: string) => void;
  rtl: boolean;
}) {
  return (
    <div>
      <p className="mb-3 font-display text-[17px] font-bold text-text-primary">{text}</p>
      <div className="space-y-2.5">
        {options.map((o) => {
          const selected = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              className={cn(
                "min-h-12 w-full rounded-ds-md border px-5 py-3 text-[16px] transition-colors duration-ds-fast",
                rtl ? "text-right" : "text-left",
                selected
                  ? "border-transparent bg-amber-100 text-text-primary ring-1 ring-inset ring-amber-600"
                  : "border-hairline-default bg-surface text-text-primary hover:bg-cream-100",
              )}
              aria-pressed={selected}
            >
              {o.label_ar}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ParentCareerInput({
  id,
  value,
  onChange,
  placeholder,
  noneLabel,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  noneLabel: string;
  invalid: boolean;
}) {
  const isNone = value === noneLabel;
  return (
    <div className="space-y-2">
      <Input className={DS_INPUT}
        id={id}
        value={isNone ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={120}
        placeholder={placeholder}
        aria-invalid={invalid}
        disabled={isNone}
      />
      <button
        type="button"
        onClick={() => onChange(isNone ? "" : noneLabel)}
        aria-pressed={isNone}
        className={cn(
          "inline-flex min-h-12 items-center rounded-ds-pill border px-4 font-display text-[13px] font-bold transition-colors duration-ds-fast",
          isNone
            ? "border-transparent bg-amber-100 text-text-primary ring-1 ring-inset ring-amber-600"
            : "border-hairline-default bg-surface text-brown-700 hover:bg-cream-100",
        )}
      >
        {noneLabel}
      </button>
    </div>
  );
}

function ItemScreen({
  lang,
  item,
  section,
  sectionIndex,
  answeredCount,
  totalCount,
  initialDraft,
  saving,
  onBack,
  onNext,
  statusOf,
  answeredBySection,
  onJumpSection,
  topBar,
  guest = false,
}: {
  topBar?: React.ReactNode;
  guest?: boolean;
  lang: Lang;
  item: Item;
  section: (typeof SECTIONS)[number];
  sectionIndex: number;
  answeredCount: number;
  totalCount: number;
  initialDraft: unknown;
  saving: boolean;
  onBack?: () => void | Promise<void>;
  onNext: (draft: unknown, ms: number) => void | Promise<void>;
  statusOf: (sid: string) => SectionStatus;
  answeredBySection: (sid: string) => number;
  onJumpSection: (sid: string) => void;
}) {
  const [draft, setDraft] = useState<unknown>(initialDraft);
  const shownAt = useRef<number>(Date.now());

  useEffect(() => {
    setDraft(initialDraft);
    shownAt.current = Date.now();
  }, [item.id, initialDraft]);

  const canProceed = serializeAnswer(item, draft) !== null;
  const labelSet: "likert5" | "importance5" = item.type === "importance5" ? "importance5" : "likert5";

  const itemLoc = tItem(item, lang);
  const sectionLoc = tSection(section.id, lang);
  const showStem = item.type === "importance5" && Boolean(itemLoc.stem);
  const dir = dirFor(lang);
  const qLabel = lang === "en"
    ? `Question ${sectionIndex + 1} of ${section.items.length}`
    : `السؤال ${answeredCount + 1} من ${totalCount}`;
  const nextLabel = lang === "en" ? "Next →" : "التالي ←";
  const questionHeadingRef = useScreenFocus<HTMLHeadingElement>(item.id);
  const questionLabelId = `bousala-question-${item.id}`;

  return (
    <main className="pb-8 md:pb-12" dir={dir}>
      {topBar}
      <div className="mx-auto max-w-[44rem] px-5">
        <BousalaProgressBar
          lang={lang}
          answeredCount={answeredCount}
          totalCount={totalCount}
          currentSectionId={section.id}
        />
        <div className={cn("mt-4 flex items-center justify-between gap-3")}>
          <span className="font-display text-[14px] font-bold text-amber-800">{sectionLoc.title}</span>
          <BousalaStagesMenu
            lang={lang}
            statusOf={statusOf}
            answeredBySection={answeredBySection}
            onJump={onJumpSection}
            align={isRtl(lang) ? "end" : "start"}
          />
        </div>
        <span className="sr-only">{qLabel}</span>

        <div key={item.id} className="ds-rise mt-6">
          {showStem ? (
            <p className="text-[14px] font-bold text-brown-500">{itemLoc.stem}</p>
          ) : null}

          <h1
            id={questionLabelId}
            ref={questionHeadingRef}
            tabIndex={-1}
            className="mt-2 font-display text-[21px] font-extrabold leading-[1.55] text-text-primary outline-none"
          >
            {itemLoc.text}
          </h1>

          <div className="mt-6">
            {item.type === "likert5" || item.type === "importance5" ? (
              <LikertField
                lang={lang}
                item={item}
                labelledBy={questionLabelId}
                value={typeof draft === "number" ? (draft as number) : undefined}
                onChange={(v) => setDraft(v)}
                labelSet={labelSet}
              />
            ) : item.type === "scenario" || item.type === "pair" || item.type === "iq" ? (
              <ScenarioField
                lang={lang}
                item={item}
                labelledBy={questionLabelId}
                value={typeof draft === "string" ? (draft as string) : undefined}
                onChange={(id) => setDraft(id)}
              />
            ) : item.type === "top3" ? (
              <Top3Field
                lang={lang}
                item={item}
                labelledBy={questionLabelId}
                value={Array.isArray(draft) ? (draft as string[]) : undefined}
                onChange={(ids) => setDraft(ids)}
              />
            ) : null}
          </div>
        </div>

        {guest && lang !== "en" ? (
          <p className="mt-5 text-[14px] leading-[1.7] text-brown-500">
            بدون حساب لهلّق. التقدّم بيتحفظ، والمتابعة بعدين ممكنة بأي وقت.
          </p>
        ) : null}

        {/* Mobile: the advance control stays pinned in the thumb zone. */}
        <div className="sticky bottom-0 z-10 -mx-5 mt-8 flex items-center justify-end gap-3 bg-[rgba(246,239,229,0.92)] px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-[14px] md:static md:mx-0 md:bg-transparent md:px-0 md:pb-0 md:backdrop-blur-none">
          <button
            type="button"
            onClick={() => onNext(draft, Date.now() - shownAt.current)}
            disabled={!canProceed || saving}
            className={cn(PLAY_PRIMARY, "w-full sm:w-auto")}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </main>
  );
}

/** Play top bar (this route has no site header): exit + «السابق», axis, save-and-exit. */
function PlayTopBar({
  lang,
  axisIndex,
  axisName,
  total,
  onSaveExit,
  onExit,
  busy,
  prev,
}: {
  lang: Lang;
  axisIndex: number;
  axisName: string;
  total: number;
  onSaveExit: () => void;
  onExit: () => void;
  busy: boolean;
  prev?: { onPrev?: () => void; disabled: boolean };
}) {
  const en = lang === "en";
  return (
    <div className="sticky top-0 z-20 mb-4 bg-cream-200/[.86] backdrop-blur-[14px]">
      <div className="mx-auto flex min-h-14 max-w-6xl items-center justify-between gap-2 px-3">
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onExit}
            aria-label={en ? "Exit" : "خروج"}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full text-brown-700 transition-colors duration-ds-fast hover:bg-cream-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </button>
          {prev ? (
            <button
              type="button"
              onClick={() => prev.onPrev?.()}
              disabled={prev.disabled}
              aria-label={en ? "Previous question" : "السؤال السابق"}
              className={cn(PLAY_GHOST, "px-3")}
            >
              {en ? "← Back" : "→ السابق"}
            </button>
          ) : null}
        </div>
        <span className="min-w-0 truncate text-center font-display text-[15px] font-bold text-text-primary">
          {en ? `Part ${axisIndex} of ${total} · ${axisName}` : `المحور ${axisIndex} من ${total} · ${axisName}`}
        </span>
        <button type="button" onClick={onSaveExit} disabled={busy} className={cn(PLAY_GHOST, "shrink-0 px-3")}>
          {en ? "Save & exit" : "حفظ وخروج"}
        </button>
      </div>
    </div>
  );
}

function WelcomePhase({
  preProfile,
  lang,
  saving,
  onStart,
}: {
  preProfile: Record<string, any>;
  lang: Lang;
  saving: boolean;
  onStart: () => void;
}) {
  const firstName = firstNameOf(
    typeof preProfile?.full_name === "string" ? preProfile.full_name : null,
  );
  const gender =
    preProfile?.gender === "male" || preProfile?.gender === "female"
      ? (preProfile.gender as "male" | "female")
      : null;
  return (
    <WelcomeIntro
      firstName={firstName}
      gender={gender}
      lang={lang}
      onStart={onStart}
      saving={saving}
    />
  );
}

/**
 * A Smart buyer finishing the free test continues on /portal/smart (their
 * order has just been advanced by the scoring path). Everyone else keeps
 * landing on their free report. Any failure falls back to the free report.
 */
async function openReportDestination(_attemptId: string): Promise<"smart" | "report"> {
  try {
    const state = await getSmartPortalState();
    return smartFinishDestination(state?.kind ?? null);
  } catch {
    return "report";
  }
}
